import { useState } from "react";
import type { CreateProps, RaRecord } from "react-admin";
import {
  BooleanInput,
  Create,
  DateTimeInput,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
} from "react-admin";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import GroupedPermissionsInput from "@/components/GroupedPermissionsInput";

type CreatedTenantApiKey = RaRecord & {
  rawKey?: string;
  name?: string;
};

const RawKeySuccessView = ({
  rawKey,
  name,
  onDone,
}: {
  rawKey: string;
  name?: string;
  onDone: () => void;
}) => {
  const notify = useNotify();

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      notify("API key copied to clipboard", { type: "info" });
    } catch {
      notify("Could not copy to clipboard", { type: "warning" });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        API key created
      </Typography>
      {name ? (
        <Typography sx={{ mb: 2 }} color="text.secondary">
          {name}
        </Typography>
      ) : null}
      <Alert severity="warning" sx={{ mb: 2 }}>
        Copy this key now. It will not be shown again.
      </Alert>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField fullWidth value={rawKey} InputProps={{ readOnly: true }} size="small" />
        <Button variant="outlined" onClick={copyKey}>
          Copy
        </Button>
      </Stack>
      <Button variant="contained" onClick={onDone}>
        Done
      </Button>
    </Box>
  );
};

const TenantApiKeysCreate = (props: CreateProps) => {
  const [created, setCreated] = useState<CreatedTenantApiKey | null>(null);
  const redirect = useRedirect();

  if (created?.rawKey) {
    return (
      <RawKeySuccessView
        rawKey={created.rawKey}
        name={created.name}
        onDone={() => redirect("list", "tenant-api-keys")}
      />
    );
  }

  return (
    <Create
      {...props}
      mutationOptions={{
        onSuccess: (data) => {
          setCreated(data as CreatedTenantApiKey);
        },
      }}
    >
      <SimpleForm>
        <TextInput source="name" required fullWidth />
        <TextInput
          source="appType"
          label="App type"
          helperText="Optional label, e.g. RELAY or OTHER"
          fullWidth
        />
        <GroupedPermissionsInput
          source="permissions"
          reference="tenant-api-keys/permissions/available"
        />
        <BooleanInput source="isActive" defaultValue />
        <DateTimeInput source="expiresAt" fullWidth />
      </SimpleForm>
    </Create>
  );
};

export default TenantApiKeysCreate;
